import PropTypes from 'prop-types';
import React, { Component, Fragment } from 'react';
import noop from 'lodash/noop';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import values from 'lodash/values';
import { connect } from 'react-redux';
import { bindActionCreators, compose } from 'redux';
import Button from 'yoda-core-components/lib/components/Button';
import MessageBox from 'yoda-core-components/lib/components/MessageBox/MessageBox';
import Loader from 'yoda-core-components/lib/components/Loader/Loader';
import withReducerSaga from 'yoda-site-components/lib/hoc/ReducerRegistry/withReducerSaga';
import CopyListReducer from 'yoda-account-components/lib/reducers/CopyListReducer';
import watchCopyListSaga from 'yoda-account-components/lib/sagas/CopyListSaga';

import classNames from 'classnames/bind';

// actions
import siteActions from 'yoda-site-components/lib/actions/AnalyticsAction';
import * as actions from 'yoda-account-components/lib/actions/CopyListActions';

import {
    selectCopyListById,
    selectCopyListPreferences,
    selectCopyListMessagesTexts,
} from 'yoda-account-components/lib/selectors/copyListSelector';
import { dt } from 'yoda-core-components/lib/helpers/Utils/GetTailwindToken';
import Input from 'yoda-core-components/lib/components/Input/Input';
import * as styles from './CopyListPanel.css';

const cx = classNames.bind(styles);

const config = {
    position: {
        BOTTOMRIGHT: 'bottomright',
        BOTTOM: 'bottom',
    },
    regex: {
        listName: /^.{4,24}$/,
    },
};

export class CopyListPanel extends Component {
    static propTypes = {
        actions: PropTypes.shape({
            copyListSubmit: PropTypes.func,
            togglePopUp: PropTypes.func,
            closePopUp: PropTypes.func,
            openModelEvent: PropTypes.func,
        }),
        sourceListId: PropTypes.string,
        sourceListName: PropTypes.string,
        sourceAccountId: PropTypes.string,
        theme: PropTypes.string,
        copyList: PropTypes.oneOfType([PropTypes.object]),
        preferences: PropTypes.oneOfType([PropTypes.object]),
        messagesTexts: PropTypes.oneOfType([PropTypes.object]),
        position: PropTypes.oneOf(values(config.position)),
        source: PropTypes.string,
        onClose: PropTypes.func,
        onSuccess: PropTypes.func,
    };

    static defaultProps = {
        sourceListId: '',
        sourceListName: '',
        sourceAccountId: '',
        actions: {
            copyListSubmit: noop,
            togglePopUp: noop,
            closePopUp: noop,
            openModelEvent: noop,
        },
        theme: '',
        copyList: {},
        preferences: {},
        messagesTexts: {},
        position: config.position.BOTTOM,
        source: '',
        onClose: () => {},
        onSuccess: () => {},
    };

    static getDerivedStateFromProps(nextprops, state) {
        if (nextprops.copyList.isOpen && !state.prevOpen) {
            return { prevOpen: true };
        }
        if (state.prevOpen && !nextprops.copyList.isOpen) {
            return { prevOpen: false, closed: true };
        }
        return null;
    }

    constructor(props) {
        super(props);
        this.renderForm = this.renderForm.bind(this);
        this.setWrapperRef = this.setWrapperRef.bind(this);
        this.handleClickOutside = this.handleClickOutside.bind(this);
        this.state = {
            showError: false,
            title: '',
            prevOpen: false,
            closed: false,
            setMyListNameLocal: props.sourceListName,
        };
    }

    componentDidMount() {
        this.toggleToolTip();
        document.addEventListener('click', this.handleClickOutside);
    }

    componentDidUpdate() {
        if (this.state.closed) {
            this.props.onClose && this.props.onClose();
        }
    }

    componentWillUnmount() {
        const {
            copyList: { isOpen },
        } = this.props;
        document.removeEventListener('click', this.handleClickOutside);
        isOpen && this.toggleToolTip();
    }

    handleClickOutside(event) {
        /* istanbul ignore if */
        if (this.wrapperRef && !this.wrapperRef.contains(event.target)) {
            const {
                copyList: { isOpen },
            } = this.props;
            isOpen && this.toggleToolTip();
        }
    }

    setWrapperRef(node) {
        this.wrapperRef = node;
    }

    copyListChange = (e) => {
        e.preventDefault();
        const name = e.target.value.trim();
        // validation
        const { messagesTexts } = this.props;
        if (!config.regex.listName.test(name)) {
            if (name.length > 24) {
                this.setState({ showError: true, title: messagesTexts.invalidListNameMessage });
                return;
            }
        } else {
            this.setState({ showError: false });
        }
        this.setState({ setMyListNameLocal: e.target.value });
    };

    copyListSubmit = (e) => {
        e.preventDefault();
        const name = get(e.target, 'copyListName.value', '').trim();
        // validation
        const { messagesTexts } = this.props;
        if (!name) {
            this.setState({ showError: true, title: messagesTexts.emptyListNameMessage });
            return;
        }

        if (!config.regex.listName.test(name)) {
            this.setState({ showError: true, title: messagesTexts.invalidListNameMessage });
            return;
        }

        // Form is validated
        // lets move forward
        this.resetError();
        // call action
        const {
            actions: { copyListSubmit },
            sourceListId,
            sourceAccountId,
            source,
        } = this.props;
        copyListSubmit(sourceListId, sourceAccountId, { name }, source);
        this.wrapperRef = null;
    };

    resetError = () => {
        // lets reset if everything looks good
        if (this.state.showError) this.setState({ showError: false });
    };

    toggleToolTip = () => {
        const {
            actions: { togglePopUp, openModelEvent },
            copyList: { isOpen },
            preferences: { openEventName: name = 'openModalCopyList' },
        } = this.props;
        this.resetError();
        togglePopUp(this.props.sourceListId);
        // track event if not already open
        !isOpen && typeof openModelEvent === 'function' && openModelEvent({ name });
    };

    renderForm(message) {
        const {
            sourceListId,
            messagesTexts,
            preferences: { errorMessages = {} },
        } = this.props;
        const { showError, title, setMyListNameLocal } = this.state;
        const { isLoading } = this.props.copyList;
        const warningmessage = get(errorMessages, 'SRV_LIST_NAME_RESTRICTIONS', '');

        let errorMessage = '';
        let showErr = false;
        if (message) {
            errorMessage = message;
            showErr = true;
        } else if (title) {
            errorMessage = title;
            showErr = showError;
        }
        return (
            <>
                <form onSubmit={this.copyListSubmit} noValidate>
                    <fieldset disabled={isLoading} className={dt(['flex', 'flex-col'])}>
                        <legend
                            className={dt([
                                'mb-4',
                                'text-medium',
                                'font-semibold',
                                'text-black',
                                'text-left',
                            ])}
                        >
                            {messagesTexts.formLegand}
                        </legend>
                        {message && (
                            <MessageBox
                                contentClassName={dt(['!w-11/12'])}
                                type="error"
                                title={warningmessage}
                                level="section"
                                automationId="list-notification-message"
                                hasClose={false}
                            />
                        )}
                        <div className={dt(['text-center', 'flex', 'justify-center'])}>
                            <Input
                                name="copyListName"
                                type="text"
                                theme={dt([
                                    'w-full',
                                    'h-12',
                                    'text-black',
                                    'text-normal',
                                    'py-3',
                                    'px-5',
                                    'border',
                                    'border-solid',
                                    'border-gray-60',
                                    'border-r-0',
                                    'outline-none',
                                    'rounded-l-lg',
                                    'rounded-r-none',
                                    'disabled:opacity-70',
                                    showErr && 'border-error-border',
                                    showErr && 'border-2',
                                ])}
                                data-automation-id={`copy-list-input-${sourceListId}`}
                                onChange={this.copyListChange}
                                value={setMyListNameLocal}
                            />
                            <Button
                                type="submit"
                                isDisabled={isLoading}
                                size="Md"
                                automationId={`copy-list-submit-${sourceListId}`}
                                className={dt(['w-[80px]', 'rounded-r-lg', 'rounded-l-none'])}
                            >
                                {isLoading ? (
                                    <Loader
                                        loaderClass={cx('btnLoading')}
                                        themeConfig={{
                                            loader: cx('loader'),
                                            wrapper: dt(['font-open-sans-bold']),
                                        }}
                                    />
                                ) : (
                                    messagesTexts.formButtonText
                                )}
                            </Button>
                        </div>
                    </fieldset>
                </form>
                {showErr && (
                    <div data-automation-id="message">
                        <p
                            className={dt(['text-small', 'text-error-border'])}
                            data-automation-id="error-message"
                        >
                            {errorMessage}
                        </p>
                    </div>
                )}
            </>
        );
    }

    renderErrorMessage = () => {
        const {
            preferences: { errorMessages = {} },
            copyList: { error },
        } = this.props;

        // default message
        let message = get(errorMessages, 'DEFAULT', '');
        const serviceErrorCode = error.errorCode;

        // if we have FE mapping to overrride message for errorCode
        if (errorMessages[serviceErrorCode]) {
            message = errorMessages[serviceErrorCode];
        } else if (error.errorMessage) {
            // Otherwise Error from Service
            message = error.errorMessage;
        }
        // If both doesnt match, default message will be taken
        return message;
    };

    renderToolTipContent = (isError, isSuccess) => {
        let message = '';
        if (isError) {
            message = this.renderErrorMessage();
        }
        if (isSuccess) {
            message = this.renderSuccessMessage();
        }
        return this.renderForm(message);
    };

    renderSuccessMessage = () => {
        const { onSuccess, copyList } = this.props;
        const id = get(copyList, 'response.id');
        const name = get(copyList, 'response.name');
        this.toggleToolTip();
        onSuccess && id && onSuccess(id, name);
    };

    render() {
        const {
            copyList: { isOpen, error, response },
            position,
        } = this.props;
        const isError = !isEmpty(error);
        const isSuccess = !isEmpty(response);
        let positionStyle = '';
        position === 'bottom'
            ? (positionStyle = dt([
                  'top-[18px]',
                  '-translate-x-1/2',
                  'smOnly:-translate-x-[calc(100%-56px)]',
                  'after:left-[calc(90%-142px)]',
                  'after:-top-[6px]',
                  'smOnly:after:left-[calc(90%-40px)]',
              ]))
            : (positionStyle = dt([
                  'top-[calc(100%+8px)]',
                  'left-0',
                  'after:left-5',
                  'after:-top-2',
              ]));

        return isOpen ? (
            <div
                className={`${cx(this.props.theme, 'copyListWrapper')} ${dt([
                    'inline-block',
                    'relative',
                ])}`}
                ref={this.setWrapperRef}
                data-automation-id="copyListWrapper"
            >
                <div
                    className={`${cx(
                        'tooltip',
                        { error: isError, success: isSuccess },
                        positionStyle
                    )} ${dt([
                        'absolute',
                        'block',
                        'bg-white',
                        'min-h-[120px]',
                        'w-[311px]',
                        'p-4',
                        'overflow-visible',
                        'rounded',
                        'border',
                        'border-solid',
                        'border-gray-15',
                        'z-[2]',
                        'smOnly:w-[calc(100vw-48px)]',
                        'after:block',
                        'shadow-elevation1',
                        'after:absolute',
                        'after:h-[14px]',
                        'after:w-[14px]',
                        'after:border-t',
                        'after:border-solid',
                        'after:border-t-gray-15',
                        'after:border-l',
                        'after:border-l-gray-15',
                        'after:rotate-45',
                    ])}`}
                >
                    {this.renderToolTipContent(isError, isSuccess)}
                </div>
            </div>
        ) : (
            ''
        );
    }
}

export const makeMapStateToProps = (state, props) => ({
    copyList: selectCopyListById(state, props),
    preferences: selectCopyListPreferences(state),
    messagesTexts: selectCopyListMessagesTexts(state),
});

export const mapDispatchToProps = (dispatch) => ({
    actions: bindActionCreators({ ...siteActions, ...actions }, dispatch),
});

export default compose(
    withReducerSaga([CopyListReducer], [watchCopyListSaga]),
    connect(makeMapStateToProps, mapDispatchToProps)
)(CopyListPanel);
